import { forwardRef } from 'react';
import {
  DegreeCardDetailed,
  type DegreeCardDetailedProps,
} from '@highereducation/hen-ui';
import type { Result } from '../../types/result';
import { useComparisonForProgram } from './useComparisonForProgram';

/**
 * Distributive variant of `Omit` — applies `Omit` to each member of a
 * union individually so discriminated-union variants stay separate.
 * Used here because `DegreeCardDetailedProps` is a union over the
 * `readMoreBehavior` discriminator, and the stock `Omit` collapses
 * the union when computing `keyof`.
 */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

/**
 * Props for {@link ComparisonAwareDegreeCard}. Mirrors `DegreeCardDetailedProps`
 * minus the four compare-related fields (the wrapper supplies those via
 * `ComparisonProvider`) and adds:
 *
 * - `program`: source-of-truth `Result` whose `programId` / `schoolId` drive
 *   the compare state, the analytics payload, and the provider mutations.
 * - `compare`: optional escape hatch. Pass `false` to suppress the compare
 *   checkbox even when a `ComparisonProvider` ancestor is present (e.g. a
 *   post-submission popup where comparing is a confused UX). Defaults to
 *   `true`. When no provider is in the tree, the checkbox is omitted
 *   regardless of this flag.
 */
export type ComparisonAwareDegreeCardProps = DistributiveOmit<
  DegreeCardDetailedProps,
  'compare' | 'compareChecked' | 'compareDisabled' | 'onCompareChange'
> & {
  program: Result;
  compare?: boolean;
};

/**
 * Thin wrapper that makes any `DegreeCardDetailed` consumer compare-ready
 * without forcing callers to wire `ComparisonProvider` props by hand. The
 * card renders identically to a raw hen-ui `DegreeCardDetailed`, plus its
 * compare quartet (`compare` / `compareChecked` / `compareDisabled` /
 * `onCompareChange`) is sourced from `useComparisonForProgram`.
 *
 * Behavior:
 * - With a `ComparisonProvider` ancestor: the checkbox reflects and mutates
 *   the shared selection, honors the max-3 cap (disabled when at-cap and
 *   not selected), and emits `CompareAdded` / `CompareRemoved` Tagular
 *   beams on toggle.
 * - Without a `ComparisonProvider` ancestor: the checkbox is hidden, no
 *   beams fire, and the card renders as a plain `DegreeCardDetailed`.
 * - With `compare={false}` (and any provider state): the checkbox is
 *   forcibly hidden and the analytics path is short-circuited. Used by
 *   post-submission popups (Recommender, Spotlight) where comparing is
 *   confused UX because the user has already picked a school.
 */
export const ComparisonAwareDegreeCard = forwardRef<
  HTMLDivElement,
  ComparisonAwareDegreeCardProps
>(({ program, compare: compareEnabled = true, ...rest }, ref) => {
  const comparisonProps = useComparisonForProgram(program);

  if (!compareEnabled) {
    return (
      <DegreeCardDetailed
        ref={ref}
        {...rest}
        compare={false}
        compareDisabled={false}
      />
    );
  }

  return <DegreeCardDetailed ref={ref} {...rest} {...comparisonProps} />;
});

ComparisonAwareDegreeCard.displayName = 'ComparisonAwareDegreeCard';
